/**
 * Dónde viven los tokens de sesión.
 *
 * Decisión (ver ADR): `localStorage`, no cookie.
 *
 * La opción correcta en abstracto es una cookie `HttpOnly; Secure; SameSite`,
 * porque un XSS no la puede leer. Pero la API de #21 **devuelve los tokens en
 * el cuerpo del JSON** y no setea ninguna cookie; implementarlo del lado del
 * cliente no cambia nada — el token igual pasa por JavaScript. Elegir
 * `sessionStorage` tampoco ayuda: rompe la sesión al abrir una pestaña nueva,
 * que en un dashboard operativo se usa todo el día.
 *
 * Lo que sí se hace para acotar el daño:
 *
 * - **Un solo lugar** que lee y escribe tokens. Ningún otro módulo toca
 *   `localStorage` para esto.
 * - **Clave versionada**: cambiar el formato invalida las sesiones viejas en
 *   vez de leerlas mal.
 * - **Rotación honesta**: el refresh nuevo se persiste antes de resolver, y
 *   el viejo deja de existir. La API detecta la reutilización y cierra todo.
 * - **Sincronía entre pestañas** por el evento `storage`: si una pestaña
 *   rota o cierra sesión, las demás se enteran en el acto.
 *
 * Cuando la API emita cookies (issue de hardening), este módulo es lo único
 * que cambia.
 */

const STORAGE_KEY = 'xyvona.dashboard.session.v1';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. Derivado de `expires_in` al momento de recibir el par. */
  accessExpiresAt: number;
  refreshExpiresAt: number;
}

/** Lo que devuelve `POST /auth/login` y `POST /auth/refresh`. */
export interface TokenPairResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

export function sessionFromPair(pair: TokenPairResponse, now: number): StoredSession {
  return {
    accessToken: pair.access_token,
    refreshToken: pair.refresh_token,
    accessExpiresAt: now + pair.expires_in * 1000,
    refreshExpiresAt: now + pair.refresh_expires_in * 1000,
  };
}

export interface TokenStore {
  read(): StoredSession | null;
  write(session: StoredSession): void;
  clear(): void;
  /** Notifica cambios hechos por **otra pestaña**. Devuelve el desuscriptor. */
  subscribe(listener: (session: StoredSession | null) => void): () => void;
}

function isStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['accessToken'] === 'string' &&
    typeof v['refreshToken'] === 'string' &&
    typeof v['accessExpiresAt'] === 'number' &&
    typeof v['refreshExpiresAt'] === 'number'
  );
}

/** Store sobre `localStorage`. Es el de producción. */
export function createTokenStore(storage: Storage = localStorage): TokenStore {
  const listeners = new Set<(session: StoredSession | null) => void>();

  const read = (): StoredSession | null => {
    let raw: string | null;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      // Safari en modo privado tira al leer. Sin sesión persistida, pero la
      // aplicación tiene que arrancar igual.
      return null;
    }
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return isStoredSession(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    const next = read();
    for (const listener of listeners) listener(next);
  };

  return {
    read,
    write(session) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(session));
      } catch {
        // Cuota llena o storage bloqueado: la sesión vive en memoria hasta
        // recargar. Peor que persistirla, mejor que no dejar entrar.
      }
    },
    clear() {
      try {
        storage.removeItem(STORAGE_KEY);
      } catch {
        /* ídem */
      }
    },
    subscribe(listener) {
      if (listeners.size === 0 && typeof window !== 'undefined') {
        window.addEventListener('storage', onStorage);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && typeof window !== 'undefined') {
          window.removeEventListener('storage', onStorage);
        }
      };
    },
  };
}

/** Store en memoria. Para tests y para render fuera del navegador. */
export function createMemoryTokenStore(initial: StoredSession | null = null): TokenStore {
  let current = initial;
  const listeners = new Set<(session: StoredSession | null) => void>();
  return {
    read: () => current,
    write(session) {
      current = session;
    },
    clear() {
      current = null;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
